import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faEyeSlash } from "@fortawesome/free-solid-svg-icons/faEyeSlash";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons/faTriangleExclamation";

import { AtiraIcon } from "../../../components/AtiraIcon";
import { Flex } from "../../../components/Flex";
import { Info } from "../../../components/Info";
import { FontSize, Text } from "../../../components/Text";
import { Entry } from "../../../model/entry/Entry";
import { EntryKind } from "../../../model/entry/types/EntryKind.enum";
import { Rounded } from "../../../theme/Rounded";
import { Spacing } from "../../../theme/Spacing";
import { Tooltip } from "../../../utils/local-antd";
import {
  DuplicateEntryFieldDescriptor,
  isValueCovered,
} from "../utils/duplicateEntryFields";
import {
  ActionButton,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  GhostButton,
  TooltipHost,
} from "./DuplicateCardPrimitives";
import { EntryDuplicateFieldCompareRow } from "./EntryDuplicateFieldCompareRow";
import { EntryKindBadge } from "./EntryKindBadge";
import { EntryMetaStrip } from "./EntryMetaStrip";
import { EntryPreviewButton } from "./EntryPreviewButton";
import { MarkedForDeletionBadge } from "./MarkedForDeletionBadge";
import { TestIDs } from "../../../tests/types/testIds";

const SameChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.s};
  border-radius: ${Rounded.pill};
  background-color: ${({ theme }) => theme.sub};
  font-size: ${FontSize.xs};
`;

// 100cqh is the height of the drawer's scrolling area, so a capped card always
// fits in it whole.
const CappedCard = styled(Card)`
  max-height: max(29rem, 100cqh);

  ${CardHeader}, ${CardFooter} {
    padding-block: ${Spacing.s};
  }

  ${CardBody} {
    gap: ${Spacing.s};
    padding-block: ${Spacing.s};
  }
`;

const FooterButtonHost = styled(TooltipHost)`
  flex: 1;
  min-width: 0;

  button {
    width: 100%;
  }
`;

type Props = {
  entry: Entry;
  finalEntry: Entry;
  descriptors: DuplicateEntryFieldDescriptor[];
  canMakeFinal: boolean;
  canExclude: boolean;
  onUpdate: ATVoidFunction<{
    field: string;
    value: string;
    mode: "append" | "set";
  }>;
  onExclude: ATVoidFunction<string>;
  onMakeFinal: ATVoidFunction<string>;
  onPreview: ATVoidFunction<string>;
};

export const EntryDuplicateCard: React.FC<Props> = ({
  entry,
  finalEntry,
  descriptors,
  canMakeFinal,
  canExclude,
  onUpdate,
  onExclude,
  onMakeFinal,
  onPreview,
}) => {
  const { t } = useTranslation();

  const otherFields = descriptors.filter(
    (descriptor) => descriptor.actionable && descriptor.getValue(entry),
  );
  const sameFields = otherFields.filter((descriptor) =>
    isValueCovered(
      descriptor.getValue(finalEntry),
      descriptor.getValue(entry),
      descriptor,
    ),
  );
  const differingFields = otherFields.filter(
    (descriptor) => !sameFields.includes(descriptor),
  );

  const title =
    entry.fields?.name ||
    entry.fields?.email ||
    entry.fields?.phone ||
    t("entries.findDuplicates.card.unnamed");

  return (
    <CappedCard
      data-testid={TestIDs.entries.duplicates.resolve.otherCard(entry._id)}
    >
      <CardHeader>
        <Flex flexDirection="column" gap="s" minWidth={0}>
          <Flex alignItems="center" gap="s" flexWrap="wrap">
            <Text fontSize="m" fontWeight="bold" wordBreak="break-word">
              <bdi>{title}</bdi>
            </Text>
            <EntryKindBadge kind={entry.kind} />
            {entry.markedForDeletion ? <MarkedForDeletionBadge /> : null}
          </Flex>

          <EntryMetaStrip entry={entry} descriptors={descriptors} />
        </Flex>

        <EntryPreviewButton entryId={entry._id} onPreview={onPreview} />
      </CardHeader>

      <CardBody>
        {entry.kind === EntryKind.JUNK ? (
          <Info
            info={t("entries.duplicates.card.junkWarning")}
            icon={faTriangleExclamation}
            color="warning"
          />
        ) : null}

        {sameFields.length ? (
          <Flex alignItems="center" gap="s" flexWrap="wrap">
            <Text fontSize="xs" color="darkerSub">
              {t("entries.duplicates.card.sameAsKept")}:
            </Text>
            {sameFields.map((descriptor) => (
              <SameChip key={descriptor.key}>
                <AtiraIcon icon={faCheck} color="success" size="xs" />
                {descriptor.label}
              </SameChip>
            ))}
          </Flex>
        ) : null}

        {differingFields.length ? (
          <Flex flexDirection="column" gap="s">
            {differingFields.map((descriptor) => (
              <EntryDuplicateFieldCompareRow
                key={descriptor.key}
                descriptor={descriptor}
                entry={entry}
                finalEntry={finalEntry}
                onUpdate={onUpdate}
              />
            ))}
          </Flex>
        ) : (
          <Text fontSize="s" color="darkerSub">
            {t("entries.duplicates.card.noDifferences")}
          </Text>
        )}
      </CardBody>

      <CardFooter>
        <Tooltip
          title={
            canMakeFinal
              ? t("entries.duplicates.card.keepThis.tooltip")
              : t("entries.duplicates.card.keepThis.disabledTooltip")
          }
        >
          <FooterButtonHost>
            <ActionButton
              fontSize="xs"
              icon={faCheck}
              disabled={!canMakeFinal}
              onClick={() => onMakeFinal(entry._id)}
            >
              {t("entries.duplicates.card.keepThis")}
            </ActionButton>
          </FooterButtonHost>
        </Tooltip>

        <Tooltip
          title={
            canExclude
              ? t("entries.duplicates.card.exclude.tooltip")
              : t("entries.duplicates.card.exclude.disabledTooltip")
          }
        >
          <FooterButtonHost>
            <GhostButton
              fontSize="xs"
              icon={faEyeSlash}
              disabled={!canExclude}
              onClick={() => onExclude(entry._id)}
            >
              {t("entries.duplicates.card.exclude")}
            </GhostButton>
          </FooterButtonHost>
        </Tooltip>
      </CardFooter>
    </CappedCard>
  );
};
